import React, {useCallback, useEffect, useState} from 'react';
import {compose} from 'recompose';
import {withDatabase} from '@nozbe/watermelondb/react';
import withState from 'src/redux/wrapper';
import {withObservables} from '@nozbe/watermelondb/react';
import {Q} from '@nozbe/watermelondb';
import NoResults from 'src/common-components/noResults';
import HistorySessionItem from 'src/modules/patients/components/history-session-item';
import {of} from 'rxjs';
import {endOfDay, startOfDay} from 'date-fns';
import {useDatabase} from '@nozbe/watermelondb/react';
import {rrulestr} from 'rrule';
import moment from 'moment';
import {Session} from 'src/models';
import {useSessionDateSync} from 'src/hooks/useSessionDateSync';
import Spinner from 'react-native-loading-spinner-overlay';

const StaffUpcomingSessions = ({
  sessions,
  filters,
  setExportItems,
  role,
  profile,
  deleteCallback,
  user,
  assignedClients,
}: any) => {
  const database = useDatabase();
  const [items, setItems] = useState<any[]>([]);
  const {isLoading, syncDateRange} = useSessionDateSync();

  const recalculateAppointments = async () => {
    const locationQuery = [];
    if (filters?.locations && filters.locations.length) {
      locationQuery.push(Q.where('location', Q.oneOf(filters.locations)));
    }

    const clients = [];
    for (const client of filters.patients) {
      clients.push(client);
    }

    let clientQueries: [Q.On[]] | any[] = [];
    if (clients.length) {
      clientQueries = [Q.where('patient_id', Q.oneOf(clients))];
    } else if (role?.patientAssignedOnly) {
      const assignedClientIds = assignedClients?.map(
        (client: any) => client.id,
      );
      clientQueries = [Q.where('patient_id', Q.oneOf(assignedClientIds))];
    } else {
      clientQueries = [];
    }

    let billingCodeQueries = [];
    if (filters?.types && filters.types.length) {
      const types = [];
      for (const code of filters.types) {
        types.push(Q.where('cpt', Q.includes(code)));
      }
      billingCodeQueries.push(Q.or(types));
    }

    const statusQuery = [];
    if (filters?.status && filters.status.length) {
      const statuses = [];
      for (const status of filters.status) {
        if (status === 'not-started') {
          statuses.push(Q.where('start_timestamp', Q.eq(null)));
        } else if (status === 'in-progress') {
          statuses.push(
            Q.and(
              Q.where('start_timestamp', Q.notEq(null)),
              Q.where('end_timestamp', Q.eq(null)),
            ),
          );
        } else if (status === 'missing-values') {
          statuses.push(
            Q.and(
              Q.where('end_timestamp', Q.notEq(null)),
              Q.where('submission_timestamp', Q.eq(null)),
            ),
          );
        } else if (status === 'completed') {
          statuses.push(Q.where('submission_timestamp', Q.notEq(null)));
        }
      }
      statusQuery.push(Q.or(...statuses));
    }

    const queriedAppointments = await database
      .get('appointments')
      .query(
        Q.where('_partition', profile?.partition),
        Q.or(
          Q.and(
            Q.where('rrule', ''),
            Q.where('date', Q.gt(startOfDay(filters.startDate).getTime())),
            Q.where('date', Q.lte(endOfDay(filters.endDate).getTime())),
            Q.where('deleted_at', null),
          ),
          Q.and(
            Q.where('rrule', Q.notEq('')),
            Q.where('deleted_at', null),
            Q.or(
              Q.and(
                Q.where(
                  'start_timestamp',
                  Q.gte(startOfDay(filters.startDate).getTime()),
                ),
                Q.where('start_timestamp', Q.lt(filters.endDate.getTime())),
              ),
              Q.and(
                Q.where(
                  'start_timestamp',
                  Q.lte(startOfDay(filters.startDate).getTime()),
                ),
                Q.or(
                  Q.where('end_timestamp', Q.eq(0)),
                  Q.where(
                    'end_timestamp',
                    Q.gte(startOfDay(filters.startDate).getTime()),
                  ),
                ),
              ),
            ),
          ),
        ),
        Q.on(
          'participants',
          Q.and(
            Q.where('user_id', Q.eq(user.id)),
            Q.where('deleted_at', null),
            ...billingCodeQueries,
          ),
        ),
        ...locationQuery,
      );

    let sessionsQueried: any[] = [];

    for (const appointment of queriedAppointments) {
      const staffParticipants = await appointment.staffParticipants.fetch();
      const appointmentPatients = await appointment.patients.fetch();
      const patient = appointmentPatients?.[0];
      if (!patient) {
        continue;
      }
      if (appointment.rrule) {
        const rruleSet = rrulestr(appointment.rrule, {
          forceset: true,
        });
        // Accommodate Daylight Savings
        let appointmentEndDate = endOfDay(filters.endDate);
        let appointmentStartDate = startOfDay(filters.startDate);
        // Always use UTC versions of the start and end window
        const startTime = new Date(
          Date.UTC(
            appointmentStartDate.getUTCFullYear(),
            appointmentStartDate.getUTCMonth(),
            appointmentStartDate.getUTCDate(),
            0,
            0,
            0,
          ),
        );
        const endTime = new Date(
          Date.UTC(
            appointmentEndDate.getUTCFullYear(),
            appointmentEndDate.getUTCMonth(),
            appointmentEndDate.getUTCDate(),
            23,
            59,
            59,
          ),
        );

        const dayAfter = new Date(
          appointmentEndDate.getFullYear(),
          appointmentEndDate.getMonth(),
          appointmentEndDate.getDate() + 1,
          0,
          0,
          0,
        );

        const dayBefore = new Date(
          appointmentStartDate.getFullYear(),
          appointmentStartDate.getMonth(),
          appointmentStartDate.getDate() - 1,
          0,
          0,
          0,
        );

        // Get all rrule occurrences in UTC within the date range
        const allOccurrences = rruleSet
          .between(startTime, endTime, true)
          .map(rruleDate => {
            const realDate = new Date(
              rruleDate.getFullYear(),
              rruleDate.getMonth(),
              rruleDate.getDate(),
            );
            realDate.setTime(
              rruleDate.getTime() +
                rruleSet.options.dtstart.getTimezoneOffset() * 60 * 1000,
            );
            return realDate;
          });

        // Convert EXDATEs to ISO strings for exact UTC matching
        const excludedDates = new Set(
          rruleSet.exdates().map(d => d.toISOString().split('T')[0]),
        );

        for (const date of allOccurrences) {
          if (excludedDates.has(date.toISOString().split('T')[0])) {
            continue;
          }
          if (
            date.toISOString().split('T')[0] ===
            dayAfter.toISOString().split('T')[0]
          ) {
            continue;
          }
          if (
            date.toISOString().split('T')[0] ===
            dayBefore.toISOString().split('T')[0]
          ) {
            continue;
          }
          for (const staffParticipant of staffParticipants) {
            if (staffParticipant.userId === user.id) {
              let queries = [];
              if (staffParticipant?.supervision) {
                queries.push(
                  Q.where('type', 'supervision'),
                  Q.sortBy('end_timestamp', Q.desc),
                );
              } else {
                queries.push(
                  Q.where('type', 'session'),
                  Q.sortBy('end_timestamp', Q.desc),
                );
              }
              let apptSessions = await database
                .get(Session.table)
                .query(
                  Q.where('appointment_id', appointment.id),
                  Q.where('session_date', moment(date).format('YYYY-MM-DD')),
                  Q.where('deleted_at', null),
                  ...clientQueries,
                  ...statusQuery,
                  ...queries,
                )
                .fetch();

              if (apptSessions[0]) {
                sessionsQueried.push(apptSessions[0]);
              }
            }
          }
        }
      } else {
        for (const staffParticipant of staffParticipants) {
          if (staffParticipant.userId === user.id) {
            let queries = [];
            if (staffParticipant?.supervision) {
              queries.push(
                Q.where('type', 'supervision'),
                Q.sortBy('end_timestamp', Q.desc),
              );
            } else {
              queries.push(
                Q.where('type', 'session'),
                Q.sortBy('end_timestamp', Q.desc),
              );
            }
            let apptSessions = await database
              .get(Session.table)
              .query(
                Q.where('appointment_id', appointment.id),
                Q.where(
                  'session_date',
                  moment(appointment.date).format('YYYY-MM-DD'),
                ),
                Q.where('deleted_at', null),
                ...clientQueries,
                ...statusQuery,
                ...queries,
              )
              .fetch();

            if (apptSessions.length > 0) {
              sessionsQueried.push(apptSessions[0]);
            }
          }
        }
      }
    }
    const sessionItems = sessionsQueried.length
      ? sessionsQueried.sort((a, b) => b.date - a.date)
      : [];

    setItems(sessionItems);
    setExportItems(sessionItems);
  };

  useEffect(() => {
    if (filters.startDate && filters.endDate) {
      syncDateRange(filters.startDate, filters.endDate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.startDate, filters.endDate]);

  // Recalculate appointments after sync is complete
  useEffect(() => {
    if (!isLoading) {
      recalculateAppointments();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filters.startDate,
    filters.endDate,
    sessions,
    filters.locations,
    filters.types,
    filters.patients,
    filters.status,
    isLoading,
  ]);

  const renderItem = useCallback(
    item => {
      return (
        <HistorySessionItem
          key={item.id}
          showStaffParticipant={true}
          session={item}
          canEdit={role?.staffHistoryEdit}
          canDelete={role?.staffHistoryDelete}
          deleteCallback={deleteCallback}
          profile={profile}
          isSupervision={item.type === 'supervision'}
        />
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  return (
    <>
      <Spinner visible={isLoading} />
      {items.length > 0 ? (
        <>{items.map(renderItem)}</>
      ) : (
        <NoResults iconName={'calendar'} message={'No Previous Sessions'} />
      )}
    </>
  );
};

export default compose(
  withDatabase,
  withState,
  withObservables([], ({authentication, database}: any) => ({
    profile: authentication.userId
      ? database.get('users').findAndObserve(authentication.userId)
      : of(),
  })),
  withObservables([], ({profile}: any) => {
    return {
      role: profile.role,
    };
  }),
  withObservables([], ({role, profile}: any) => {
    if (role?.patientAssignedOnly) {
      return {
        assignedClients: profile.patients,
      };
    }
    return {
      assignedClients: of([]),
    };
  }),
)(StaffUpcomingSessions);
